// BookingTimeValidator.js - A utility to handle booking time validation logic
import moment from 'moment';

/**
 * Validates and enforces booking time rules based on service duration
 */
class BookingTimeValidator {
    constructor() {
        this.BUSINESS_HOURS = {
            start: 6, // 6:00 AM
            end: 21,  // 9:00 PM
        };

        this.MIN_ADVANCE_HOURS = 3; // Bookings must be at least 3 hours in advance

        // Valid service durations
        this.VALID_DURATIONS = [1, 3, 6, 9];

        // Time-based duration restrictions
        this.TIME_BASED_DURATIONS = {
            before3PM: [1, 3, 6, 9],    // Before 3 PM: all durations available
            before6PM: [1, 3, 6],       // 3 PM to 6 PM: 1, 3, 6 hours
            before9PM: [1, 3]           // 6 PM to 9 PM: 1, 3 hours
        };

        // Special booking slots for each duration (mapping service hours to start time)
        this.BOOKING_SLOTS = {
            // Same day bookings
            sameDay: {
                3: 18, // 3-hour booking must start at 6PM (18:00) to end at 9PM-12AM
                6: 15, // 6-hour booking must start at 3PM (15:00) to end at 9PM
                9: 12, // 9-hour booking must start at 12PM (12:00) to end at 9PM
                1: 20  // 1-hour booking can start as late as 8PM (20:00) to end at 9PM
            },
            // Future day bookings
            futureDay: {
                3: 21, // 3-hour booking must start at 9PM (21:00)
                6: 18, // 6-hour booking must start at 6PM (18:00)
                9: 15, // 9-hour booking must start at 3PM (15:00)
                1: 23  // 1-hour booking can start as late as 11PM (23:00)
            }
        };
    }

    /**
     * Determines if a service duration is valid
     * @param {number} hours - Service duration in hours
     * @returns {boolean} True if valid, false otherwise
     */
    isValidDuration(hours) {
        return this.VALID_DURATIONS.includes(hours);
    }

    /**
     * Checks if a date is today
     * @param {Date} date - Date to check
     * @returns {boolean} True if date is today, false otherwise
     */
    isSameDay(date) {
        const now = moment();
        return moment(date).isSame(now, 'day');
    }

    /**
     * Gets the start time for a booking based on duration and whether it's same-day
     * @param {number} duration - Service duration in hours
     * @param {boolean} isSameDay - Whether the booking is for today
     * @returns {number} Hour (0-23) for the booking to start
     */
    getStartTimeForDuration(duration, isSameDay) {
        const slotType = isSameDay ? 'sameDay' : 'futureDay';
        return this.BOOKING_SLOTS[slotType][duration] || null;
    }

    /**
     * Validates if a booking time meets all requirements
     * @param {Date} dateTime - Booking date and time
     * @param {number} duration - Service duration in hours
     * @returns {Object} Validation result with status and message
     */
    validateBookingTime(dateTime, duration) {
        if (!dateTime) {
            return { isValid: false, message: 'Please select a date and time' };
        }

        if (!this.isValidDuration(duration)) {
            return { isValid: false, message: 'Invalid service duration' };
        }

        const selectedTime = moment(dateTime);
        const now = moment();
        const isSameDay = this.isSameDay(dateTime);

        // Check if booking is in the past
        if (selectedTime.isBefore(now)) {
            return { isValid: false, message: 'Cannot book in the past' };
        }

        // Check minimum advance notice (3 hours)
        if (selectedTime.diff(now, 'hours', true) < this.MIN_ADVANCE_HOURS) {
            return { isValid: false, message: 'Booking must be at least 3 hours in advance' };
        }

        // Business hours check
        const hour = selectedTime.hour();
        if (hour < this.BUSINESS_HOURS.start || hour >= this.BUSINESS_HOURS.end) {
            return {
                isValid: false,
                message: `Booking time must be between ${this.BUSINESS_HOURS.start}:00 AM and ${this.BUSINESS_HOURS.end - 12}:00 PM`
            };
        }

        // Check if service extends past midnight
        const serviceEndHour = hour + duration;
        if (serviceEndHour > 24) {
            return {
                isValid: false,
                message: `For ${duration}-hour services, latest booking time is ${24 - duration}:00`
            };
        }

        // Special slot validation logic
        const requiredStartHour = this.getStartTimeForDuration(duration, isSameDay);
        if (requiredStartHour !== null && hour !== requiredStartHour) {
            const period = requiredStartHour >= 12 ? 'PM' : 'AM';
            const displayHour = requiredStartHour > 12 ? requiredStartHour - 12 : requiredStartHour;
            return {
                isValid: false,
                message: `${duration}-hour bookings ${isSameDay ? 'today' : 'in the future'} must start at ${displayHour}:00 ${period}`
            };
        }

        return { isValid: true, message: 'Valid booking time' };
    }

    /**
     * Returns a filter function for DatePicker to filter available time slots
     * @param {Date} date - Selected date
     * @param {number} duration - Service duration
     * @returns {Function} Filter function for time picker
     */
    getTimeFilter(date, duration) {
        if (!date || !this.isValidDuration(duration)) {
            return () => false;
        }

        const now = moment();
        const selectedDate = moment(date).startOf('day');
        const isSameDay = selectedDate.isSame(now, 'day');

        // For filtering time slots
        return (time) => {
            const hour = time.getHours();
            const minute = time.getMinutes();

            // Must be on the hour (minute = 0)
            if (minute !== 0) {
                return false;
            }

            // Business hours check
            if (hour < this.BUSINESS_HOURS.start || hour >= this.BUSINESS_HOURS.end) {
                return false;
            }

            // Check if service would extend past midnight
            if (hour + duration > 24) {
                return false;
            }

            // Create a combined date+time object for the 3-hour advance check
            const timeToCheck = new Date(selectedDate);
            timeToCheck.setHours(hour, 0, 0, 0);

            // Check minimum advance notice (3 hours)
            if (moment(timeToCheck).diff(now, 'hours', true) < this.MIN_ADVANCE_HOURS) {
                return false;
            }

            // Special slot validation for the specific duration
            const requiredStartHour = this.getStartTimeForDuration(duration, isSameDay);
            if (requiredStartHour !== null && hour !== requiredStartHour) {
                return false;
            }

            return true;
        };
    }

    /**
     * Suggests available booking slots for a given date and duration
     * @param {Date} date - Selected date
     * @param {number} duration - Service duration
     * @returns {Array} Array of valid booking slots as Date objects
     */
    getSuggestedBookingSlots(date, duration) {
        if (!date || !this.isValidDuration(duration)) {
            return [];
        }

        const selectedDate = moment(date).startOf('day');
        const isSameDay = this.isSameDay(selectedDate);
        const now = moment();

        const slots = [];
        const requiredStartHour = this.getStartTimeForDuration(duration, isSameDay);

        if (requiredStartHour !== null) {
            // Just return the one valid slot for this duration
            const slot = new Date(selectedDate);
            slot.setHours(requiredStartHour, 0, 0, 0);

            // Only add if it's 3+ hours in the future
            if (moment(slot).diff(now, 'hours', true) >= this.MIN_ADVANCE_HOURS) {
                slots.push(slot);
            }
        } else {
            // If no specific slot is required, return all valid slots
            for (let hour = this.BUSINESS_HOURS.start; hour < this.BUSINESS_HOURS.end; hour++) {
                // Skip if service would extend past midnight
                if (hour + duration > 24) continue;

                const slot = new Date(selectedDate);
                slot.setHours(hour, 0, 0, 0);

                // Only add if it's 3+ hours in the future
                if (moment(slot).diff(now, 'hours', true) >= this.MIN_ADVANCE_HOURS) {
                    slots.push(slot);
                }
            }
        }

        return slots;
    }

    /**
     * Gets available durations based on the selected time
     * @param {number} hour - Selected hour (0-23)
     * @returns {Array} Array of available durations
     */
    getAvailableDurations(hour) {
        if (hour <= 15) { // Before or at 3 PM
            return this.TIME_BASED_DURATIONS.before3PM;
        } else if (hour <= 18) { // After 3 PM and up to 6 PM
            return this.TIME_BASED_DURATIONS.before6PM;
        } else { // After 6 PM and up to 9 PM
            return this.TIME_BASED_DURATIONS.before9PM;
        }
    }

    /**
     * Checks if a duration is valid for the selected time
     * @param {number} duration - Service duration in hours
     * @param {number} hour - Selected hour (0-23)
     * @returns {boolean} True if duration is valid for the time, false otherwise
     */
    isDurationValidForTime(duration, hour) {
        const availableDurations = this.getAvailableDurations(hour);
        return availableDurations.includes(duration);
    }
}

export default BookingTimeValidator;